import { effect, inject, Injectable, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { Step } from '../models/model/concretes/components/step.model';
import { NavigationService } from './navigation.service';
import { SharedDataService } from './shared-data.service';
import { Subscription } from '../models/model/concretes/shared/subcription.model';
import { CombinedStore } from 'src/app/core/combined.store';
import { environment } from 'src/environments/environment';
import { isPartner } from '../utils/brand-utils';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  currentStep: Step | undefined;
  currentGroupStepIndex: WritableSignal<number>;
  currentStepIndex: WritableSignal<number>;

  subscription = this.sharedDataService.getSubscription();

  store = inject(CombinedStore);

  constructor(
    private sharedDataService: SharedDataService,
    private navigationService: NavigationService,
    private router: Router
  ) {
    this.currentGroupStepIndex = this.sharedDataService.getGroupStep();
    this.currentStepIndex = this.sharedDataService.getStepIndex();

    effect(
      () => {
        const index = this.sharedDataService.getStepIndex();
        if (index != null) {
          this.updateCurrentStep();
        }
      },
      { allowSignalWrites: true }
    );
  }

  //#region Public Methods
  previousBtn(): boolean {
    return this.currentStepIndex() != 0;
  }

  previousStep() {
    if (this.currentStep != undefined && this.currentStepIndex() != 0) {
      this.sharedDataService.previousStep();
      this.updateCurrentStep();
    }
  }

  nextBtn(): boolean {
    return (
      this.currentGroupStepIndex() != (this.subscription()?.groupSteps?.length ?? 0) - 1 ||
      this.currentStepIndex() !=
        (this.subscription()?.getStepsCountInGroup(this.currentGroupStepIndex()) ?? 0) - 1
    );
  }

  nextStep() {
    this.navigationService.updateSubmitted(false);

    if (this.currentStep) {
      this.currentStep.isActive = false;
      this.currentStep.isCompleted = true;
      this.subscription()?.setCurrentStep(
        this.currentGroupStepIndex(),
        this.currentStepIndex(),
        this.currentStep
      );
    }

    this.sharedDataService.forceUpdateSubscription();

    const stepCount =
      (this.subscription()?.getStepsCountInGroup(this.currentGroupStepIndex()) ?? 0) - 1;
    if (this.currentStepIndex() <= stepCount) {
      if (this.currentStepIndex() == stepCount) {
        this.sharedDataService.nextGroupStep();
        this.resetStepOfGroupStep();
      } else {
        this.sharedDataService.nextStep();
      }

      this.updateCurrentStep();
    }
  }
  //#endregion

  //#region Private Methods
  public updateCurrentStep() {
    //Mettre à jour l'étape courante avant de passer à l'étape suivante
    this.currentStep = this.subscription()?.getCurrentStep(
      this.currentGroupStepIndex(),
      this.currentStepIndex()
    );

    if (this.currentStep) {
      this.currentStep.isActive = true;
      this.subscription()?.setCurrentStep(
        this.currentGroupStepIndex(),
        this.currentStepIndex(),
        this.currentStep
      );

      this.subscription()?.activateStepByUrl(this.currentStep.url);
    }

    // Ne pas afficher l'étape "Information" du Client si le parcours est issu d'une Fiche Client
    // Afficher directement l'étape suivante
    if (this.currentStep?.url == "subscriber" && this.store.context().souscripteur?.email && !isPartner(environment)) {
      this.nextStep();
      return;
    }

    if (this.currentStep?.layoutInfo) {
      this.setHeader(this.currentStep);
      this.navigationService.setNavigationState({
        groupIndex: this.currentGroupStepIndex(),
        stepNumber: this.currentStepIndex(),
        isStepValid: false,
        subscriberCGVm: null,
        url: this.currentStep.url
      });

      this.router.navigate(['subscription', this.currentStep.url]);
    }
  }

  private setHeader(step: Step) {
    this.sharedDataService.setLayoutInfo(step.layoutInfo);
    this.sharedDataService.setStepShow(step.layoutInfo.stepShow);
    this.setMaxStep();
    this.setMaxStepShow();
  }

  public setMaxStep() {
    const groupStepIndex = this.currentGroupStepIndex();
    if (groupStepIndex !== undefined) {
      this.sharedDataService.setMaxStep(
        this.subscription()?.getStepsCountInGroup(groupStepIndex) ?? 0
      );
    }
  }

  private setMaxStepShow() {
    let maxStep = this.subscription()?.getLastStepInGroupStep(this.currentGroupStepIndex());
    if (maxStep) this.sharedDataService.setMaxStepShow(maxStep);
  }

  private resetStepOfGroupStep() {
    this.sharedDataService.setStep(1);
    this.sharedDataService.setStepIndex(0);
  }
  //#endregion
}

